/**
 * Export client helpers (BUILD_SPEC.md §6.6).
 *
 * Each helper hits one of the three export endpoints, captures the
 * filename from the response's Content-Disposition header, and drives
 * a temporary `<a download>` element to save the file. We don't use
 * the axios client for this because:
 *   1. axios defaults to JSON parsing for unknown content types —
 *      the binary XLSX body would lose bytes;
 *   2. the browser's fetch API streams the response directly into a
 *      Blob, so memory usage scales to one workbook copy regardless
 *      of size.
 *
 * The auth token still flows through — we read it from the shared
 * authStore and set the Authorization header explicitly. The session
 * cookie covers refresh; for the access token we mirror the axios
 * interceptor pattern manually.
 */

import { authStore } from "@/stores/authStore";
import type { TransactionStatus } from "./types";

const DEFAULT_FILENAME_PATTERNS: Record<ExportKind, string> = {
  transaction: "transaction.xlsx",
  snapshot: "snapshot.xlsx",
  archive: "archive.xlsx",
};

type ExportKind = "transaction" | "snapshot" | "archive";

/** Extracts `filename=...` from a Content-Disposition header. Returns
 *  the provided fallback if the header is missing or doesn't contain
 *  a quoted filename. We only support the simple quoted form because
 *  that's what the API emits. */
function filenameFromHeader(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

/** Saves a Blob with the given filename by clicking a transient anchor.
 *  Cleans up the temporary object URL on the next tick so we don't
 *  leak revocable URLs into the document's history. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Anchor must be in the DOM for Firefox; both engines remove it on
  // the synchronous click handler return.
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Shared fetch wrapper used by all three export endpoints. Throws on
 *  non-2xx so the caller's mutation surfaces it through React Query. */
async function fetchExport(kind: ExportKind, path: string): Promise<void> {
  const token = authStore.getToken();
  const res = await fetch(`/api${path}`, {
    method: "GET",
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // The API returns JSON for error responses even on these binary
    // routes; try to parse it for a clean message.
    let message = `Export failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      /* binary content; ignore */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const filename = filenameFromHeader(
    res.headers.get("content-disposition"),
    DEFAULT_FILENAME_PATTERNS[kind],
  );
  downloadBlob(blob, filename);
}

export function downloadTransactionExport(id: string): Promise<void> {
  return fetchExport("transaction", `/export/transaction/${encodeURIComponent(id)}`);
}

export function downloadSnapshotExport(status: TransactionStatus): Promise<void> {
  return fetchExport(
    "snapshot",
    `/export/snapshot?status=${encodeURIComponent(status)}`,
  );
}

export function downloadArchiveExport(): Promise<void> {
  return fetchExport("archive", "/export/archive");
}
